import {
  M_ORGANISATION_CREATE_URL,
  M_ORGANISATION_GET_BY_ID_URL,
  M_REGULAR_COLLECTION_CREATE_URLS,
  M_REGULAR_COLLECTION_GET_BY_ID_URLS,
  M_REGULAR_COLLECTION_UPDATE_URLS,
  M_REGULAR_COLLECTION_URLS,
  M_SEARCH_API_URL,
} from 'constants/apiConstants';
import { IApiWriteResponse } from 'interfaces/IApiWriteResponse';
import {
  MBase,
  MOrganisation,
  MSearchParam,
  MSearchResponse,
  MWriteBody,
} from 'interfaces/IMasterDb';

import axiosInstance from './AxiosInstance';

type MWriteResponse = IApiWriteResponse<MBase<unknown>>;

export type MWritableCollectionKey =
  keyof typeof M_REGULAR_COLLECTION_CREATE_URLS;

// M writes report failures inline, so the interceptor must not toast them.
const M_WRITE_REQUEST_CONFIG = { _silentErrors: true };

export const MasterDbService = {
  FetchMRegularCollectionData: async (
    collectionKey: keyof typeof M_REGULAR_COLLECTION_URLS,
    mOrgId: string,
    pageSize: number,
    pageIndex: number
  ) => {
    const baseUrl = M_REGULAR_COLLECTION_URLS[collectionKey];
    const url = `${baseUrl}/${mOrgId}/Size/${pageSize}/Page/${pageIndex}`;
    const response = await axiosInstance.get(url);
    return response.data;
  },

  SearchMCollectionData: async <T>(
    params: MSearchParam
  ): Promise<MSearchResponse<T>> => {
    const response = await axiosInstance.post<MSearchResponse<T>>(
      M_SEARCH_API_URL,
      params
    );
    return response.data;
  },

  /**
   * Creates an M document. The id is generated once per form and reused on
   * retry, so a failed create (a timeout that landed, or a retry rejected as
   * "already exists") is checked with GetById: if the document is there, the
   * create counts as done. Otherwise the original error stands.
   */
  CreateMDocument: async (
    collectionKey: MWritableCollectionKey,
    body: MWriteBody
  ): Promise<MWriteResponse> => {
    try {
      const response = await axiosInstance.post<MWriteResponse>(
        M_REGULAR_COLLECTION_CREATE_URLS[collectionKey],
        body,
        M_WRITE_REQUEST_CONFIG
      );
      return response.data;
    } catch (createError) {
      const existing = await MasterDbService.GetMDocument(
        collectionKey,
        body.id
      ).catch(() => null);
      if (existing) return { data: [existing], changes: [] };
      throw createError;
    }
  },

  GetMDocument: async (
    collectionKey: MWritableCollectionKey,
    id: string
  ): Promise<MBase<unknown>> => {
    const response = await axiosInstance.get<MBase<unknown>>(
      `${M_REGULAR_COLLECTION_GET_BY_ID_URLS[collectionKey]}/${id}`,
      M_WRITE_REQUEST_CONFIG
    );
    return response.data;
  },

  UpdateMDocument: async (
    collectionKey: MWritableCollectionKey,
    body: MWriteBody
  ): Promise<MWriteResponse> => {
    const response = await axiosInstance.put<MWriteResponse>(
      M_REGULAR_COLLECTION_UPDATE_URLS[collectionKey],
      body,
      {
        ...M_WRITE_REQUEST_CONFIG,
        headers: { ClientNow: new Date().toISOString() },
      }
    );
    return response.data;
  },

  GetMOrganisation: async (id: string): Promise<MOrganisation> => {
    const response = await axiosInstance.get<MOrganisation>(
      `${M_ORGANISATION_GET_BY_ID_URL}/${id}`,
      M_WRITE_REQUEST_CONFIG
    );
    return response.data;
  },

  // Response is { data: [mOrganisation, updatedUser], changes } — the server
  // adds the user's full access and morgAccesses in the same transaction.
  CreateMOrganisation: async (body: MWriteBody): Promise<MWriteResponse> => {
    const response = await axiosInstance.post<MWriteResponse>(
      M_ORGANISATION_CREATE_URL,
      body,
      M_WRITE_REQUEST_CONFIG
    );
    return response.data;
  },
};
